/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * OpenRTB 2.6 field catalog (#858) — the IAB-standard fields per body section,
 * derived from the OpenRTB DTOs (`libs/dto/src/openrtb.dto.ts`) and the 2.6 spec.
 *
 * Powers three things in the ORTB body editor:
 *  - **context-sensitive autocomplete** when adding a field to a section,
 *  - **"add recommended fields"** (standard/missing fields, VAST-parity),
 *  - **validation** warnings for a body missing important fields.
 *
 * Paths are **relative to the section** (the top-level group key), so `device`
 * offers `ua`, `ip`, `geo.country`, … and `imp` offers `tagid`, `video.maxduration`, …
 */

// Relative, not aliased: this file moved to `libs/dto` (#1315) so the driver and the `/ortb`
// controller can read the same declaration the editor does. The console reaches it through the
// `@ortb-field-catalog` alias; every module below is already in the shared subgraph.
import { isPopulatedWithoutAuthoring, mergeBaseFieldAt } from './ortb-merge-base';
import { ORTB_BODY_PATTERN_AT, ORTB_BODY_PATTERN_EACH } from './ortb-body-leaf';
import {
  ABSOLUTE_URL,
  BUNDLE_ID,
  COUNTRY_CODE,
  CURRENCY_CODE,
  FLAG_0_1,
  HOSTNAME,
  IP_ADDRESS,
  LANGUAGE_CODE,
  MIME_TYPE,
  NON_NEGATIVE,
  POSITIVE,
  normalizeProtocolVersion,
  protocolConceptForWireField,
  renderConceptMember,
  wireExpressionFor,
  wireFieldNamesFor,
  type ConceptRendering,
  type ValueConstraint,
  type ProtocolVersion,
} from './field-catalog';
import { appliesTo, type DestinationField, type DestinationFieldType, type FieldImportance } from './destination-field';

export type OrtbFieldType = DestinationFieldType;

/** The OpenRTB protocol versions the editor is version-aware about. */
export type OrtbVersion = ProtocolVersion;

/**
 * **An ORTB body path is a destination**, so its declaration is the shared one (`#1163`, `D34`).
 *
 * `OrtbFieldImportance` is gone — it was `ParamImportance` member for member, two enums for one
 * concept in two files. `path` is now `name`, the shared word for *how this destination addresses
 * the field*: a source entry's `path` is a context path we resolve **from**, and one word for both
 * is how the two catalogs came to be conflated.
 *
 * **An entry declares a `value` only where one is known** — a concept the session carries
 * (`derived`), the operator (`from: 'operator'`), or a rendered concept. The rest say nothing,
 * which is a statement of fact rather than an omission to fix in passing: recording a guess would
 * be inventing claims rather than migrating them. How many of each is asserted in
 * `ortb-field-catalog.spec.ts`, not written here, because a count in prose only goes stale.
 */
export type OrtbCatalogField = DestinationField;

/* ------------------------------------------------------------------------- *
 * Slot rules — this receiver's own, as opposed to a concept's grammar (`4a`, `#914`)
 * ------------------------------------------------------------------------- */

/**
 * **A numeric enumeration IAB owns gets a floor and no ceiling** (`4a`).
 *
 * `#914` asks for `devicetype ∈ {1..7}`, `at ∈ {1,2,3}`, `protocols ⊆ {1..14}` and four more like
 * them. Every one of those is a **closed list of somebody else's vocabulary**, which is exactly what
 * the corollary forbids — *close over a vocabulary we own, never one somebody else evolves* — and
 * the evidence is in the list itself: `devicetype` ran 1–7 in OpenRTB 2.5 and 2.6 added **8**, and
 * `at` has said *"values greater than 500 can be used for exchange-specific auction types"* since
 * 2.3. A ceiling written from today's spec reports a correct value as wrong the week the list grows,
 * and it does so on every request that carries it.
 *
 * So the declared rule is the half that is structural rather than enumerated: these are 1-based
 * identifiers, so `0` and negatives are wrong on every version and nothing else is knowable here.
 * That is a deliberately small claim, and it is the whole of what a range can honestly say.
 *
 * The alternative is not *declare the ceiling anyway*: it is `conceptMembersFor`, which already
 * gives `video.placement` and `video.linearity` version-scoped members with no enum declared at all
 * (`#1112`). Turning one of these into a concept is an **addition** when somebody wants it.
 */
export const IAB_ENUM_MEMBER: ValueConstraint = { range: { min: 1 } };

/** Auction type — 1-based, and open above for the reason {@link IAB_ENUM_MEMBER} gives. */
export const AUCTION_TYPE: ValueConstraint = IAB_ENUM_MEMBER;

/** Connection type — the one IAB enumeration whose first member is `0` (unknown). */
export const CONNECTION_TYPE: ValueConstraint = { range: { min: 0 } };

/**
 * `0` = pre-roll, a positive number = that many seconds in, `-1` = generic mid-roll, `-2` = post.
 * The floor is `-2` because there is no third negative member and never has been.
 */
export const START_DELAY: ValueConstraint = { range: { min: -2 } };

/**
 * Test mode, and the sentinel is the point of declaring it.
 *
 * `#914`: *"`test = 1` reaching a production source is the same class"* as the zeroed IFA — a
 * perfectly valid value that tells the exchange not to bill and, on most, not to return real
 * demand. Nothing about the request looks wrong; it simply never earns.
 */
export const TEST_MODE: ValueConstraint = {
  ...FLAG_0_1,
  sentinels: [
    {
      value: '1',
      means: 'puts the exchange in test mode — it will not bill, and usually will not return real demand.',
    },
  ],
};

/** Degrees of latitude. A bound both ends, because this one is not somebody's growing list. */
export const LATITUDE: ValueConstraint = { range: { min: -90, max: 90 } };

/** Degrees of longitude. */
export const LONGITUDE: ValueConstraint = { range: { min: -180, max: 180 } };

/**
 * **Content categories are declared by nothing here, and that is a decision** (`4a`).
 *
 * `#914` asks for `cat` / `bcat` / `sectioncat` against the IAB taxonomy, `IAB\d+(-\d+)?`. That is
 * the **1.0** taxonomy, and since OpenRTB 2.6 the taxonomy in force is whatever `cattax` names — 2
 * is Content 2.0, 6 is Content 2.2, and those carry bare numeric ids such as `150`, not `IAB1-2`.
 * So the grammar is not a property of the field; it is a property of a **sibling field's value**,
 * and a rule written from the 1.0 spelling would report a conformant 2.2 category as wrong.
 *
 * Declaring it becomes possible when a constraint can read `cattax` — which is `X12`'s `when`
 * widening to Criteria, where a rule may be conditioned on another value. Until then the honest
 * answer is nothing: *a false positive is worse than an omission* (`#1002`).
 *
 * **Named rather than left as an absence**, because an absence means both *we decided against this*
 * and *nobody got to it*, and no gate can tell those apart (`#1189`). `ortb-field-catalog.spec.ts`
 * reads this list and fails if one of these fields quietly acquires a rule, so reversing the
 * decision is a deliberate act rather than a population pass nobody reviewed.
 */
export const CATEGORY_FIELDS_WITHOUT_A_RULE = ['bcat', 'cat', 'content.cat'] as const;

/** Section key (top-level group in the editor) → standard fields. */
export const ORTB_FIELD_CATALOG: Record<string, OrtbCatalogField[]> = {
  // Top-level BidRequest scalar fields (each renders as its own section/leaf).
  request: [
    {
      name: 'at',
      valueType: 'number',
      importance: 'optional',
      description: 'Auction type (1=first, 2=second price)',
      constraint: AUCTION_TYPE,
    },
    {
      name: 'tmax',
      valueType: 'number',
      importance: 'optional',
      description: 'Max response time in ms',
      constraint: POSITIVE,
    },
    {
      name: 'cur',
      valueType: 'array',
      importance: 'optional',
      description: 'Allowed currencies (e.g. ["USD"])',
      constraint: CURRENCY_CODE,
    },
    {
      name: 'test',
      valueType: 'number',
      importance: 'optional',
      description: '1 = test mode (no billing)',
      constraint: TEST_MODE,
    },
    { name: 'bcat', valueType: 'array', importance: 'optional', description: 'Blocked IAB advertiser categories' },
    {
      name: 'badv',
      valueType: 'array',
      importance: 'optional',
      description: 'Blocked advertiser domains',
      constraint: HOSTNAME,
    },
    {
      name: 'bapp',
      valueType: 'array',
      importance: 'optional',
      description: 'Blocked app bundles',
      constraint: BUNDLE_ID,
    },
    { name: 'wseat', valueType: 'array', importance: 'optional', description: 'Allowed buyer seats' },
    {
      name: 'wlang',
      valueType: 'array',
      importance: 'optional',
      description: 'Allowed content languages',
      constraint: LANGUAGE_CODE,
    },
  ],

  imp: [
    /**
     * **The exchange assigns this one, so only the operator has it** (`#1186`, Todd 2026-08-27:
     * *"imp.tagid is a field we know needs to be filled — with paste, we have one to offer, from
     * scratch it's added empty"*).
     *
     * `from: 'operator'` says exactly that and already meant it: *take what a pasted tag supplied;
     * otherwise leave it unfilled and warn that no value exists.* The declaration was simply
     * missing, and its absence is what let a pasted `"tagid": "28125"` — Magnite's placement id —
     * be replaced by a macro resolving to the **publisher's ad unit**, which is a different
     * concept wearing the same field name.
     *
     * The driver still builds `imp.tagid` from the inbound ad unit, and that is right for a
     * partner who has assigned no placement: it is in the merge base, the panel says so, and an
     * authored value overrides it. What changed is that an authored one now survives being pasted.
     */
    {
      name: 'tagid',
      valueType: 'string',
      importance: 'recommended',
      description: 'Ad placement / tag ID — the id this exchange assigned you',
      value: { from: 'operator' },
    },
    {
      name: 'bidfloor',
      valueType: 'number',
      importance: 'recommended',
      description: 'Minimum bid (CPM)',
      constraint: NON_NEGATIVE,
    },
    {
      name: 'bidfloorcur',
      valueType: 'string',
      importance: 'optional',
      description: 'Bid floor currency (default USD)',
      constraint: CURRENCY_CODE,
    },
    {
      name: 'secure',
      valueType: 'number',
      importance: 'recommended',
      description: '1 = HTTPS creative required',
      constraint: FLAG_0_1,
    },
    {
      name: 'instl',
      valueType: 'number',
      importance: 'optional',
      description: '1 = interstitial',
      constraint: FLAG_0_1,
    },
    {
      name: 'exp',
      valueType: 'number',
      importance: 'optional',
      description: 'Seconds the bid is valid for prefetch',
      constraint: NON_NEGATIVE,
    },
    {
      name: 'video.mimes',
      valueType: 'array',
      importance: 'required',
      description: 'Supported MIME types (e.g. ["video/mp4"])',
      constraint: MIME_TYPE,
    },
    {
      name: 'video.minduration',
      valueType: 'number',
      importance: 'recommended',
      description: 'Min ad duration (s)',
      constraint: NON_NEGATIVE,
    },
    {
      name: 'video.maxduration',
      valueType: 'number',
      importance: 'required',
      description: 'Max ad duration (s)',
      constraint: POSITIVE,
    },
    {
      name: 'video.protocols',
      valueType: 'array',
      importance: 'required',
      description: 'Supported VAST protocols',
      constraint: IAB_ENUM_MEMBER,
    },
    {
      name: 'video.w',
      value: { from: 'derived', source: 'w' },
      valueType: 'number',
      importance: 'recommended',
      description: 'Player width (px)',
    },
    {
      name: 'video.h',
      value: { from: 'derived', source: 'h' },
      valueType: 'number',
      importance: 'recommended',
      description: 'Player height (px)',
    },
    {
      name: 'video.startdelay',
      valueType: 'number',
      importance: 'optional',
      description: '0=pre, >0=mid, -1=generic mid, -2=post',
      constraint: START_DELAY,
    },
    /**
     * **One entry, because there is one concept** (`N11h`, #1112 · `D29`).
     *
     * This was a *pair* — `video.plcmt` scoped to 2.6 and `video.placement` scoped to 2.5, both
     * `number`, each de-weighted outside its version. That is one question asked twice, and it
     * asked the operator the wrong one: it made them choose a **field name** (which is the
     * receiver's business) and then supply an **integer from an IAB list** (which is nobody's
     * idea of authoring). Choosing the wrong half of the pair produced a request that is
     * syntactically fine and semantically wrong, which nothing downstream can detect.
     *
     * Now the operator states a member — `instream`, `accompanying` — and the driver renders the
     * name and the integer for the version the source declares. The raw field stays reachable by
     * anyone who types it (a **number** at this path is left untouched, deliberately), it is
     * simply no longer *offered*: offering both is offering a validation finding (`N11g`).
     *
     * The version-awareness did not disappear, it got sharper. It is no longer *which of two
     * fields is de-weighted* but **which members this version can express** — 2.5 has a faithful
     * value for `instream` alone — and that is `conceptMembersFor`, asked at the row.
     */
    {
      name: 'video.placement',
      valueType: 'string',
      importance: 'recommended',
      description: 'Where the ad sits relative to the content it plays with',
      value: { from: 'concept', concept: 'placement' },
    },
    {
      name: 'video.linearity',
      valueType: 'string',
      importance: 'optional',
      description: 'Whether the ad plays in the content stream or over it',
      value: { from: 'concept', concept: 'linearity' },
    },
    {
      name: 'video.skip',
      valueType: 'number',
      importance: 'optional',
      description: '1 = skippable',
      constraint: FLAG_0_1,
    },
    {
      name: 'video.playbackmethod',
      valueType: 'array',
      importance: 'optional',
      description: 'Playback methods',
      constraint: IAB_ENUM_MEMBER,
    },
    {
      name: 'video.playbackend',
      valueType: 'number',
      importance: 'optional',
      description: 'Playback cessation mode',
      constraint: IAB_ENUM_MEMBER,
    },
    {
      name: 'video.api',
      valueType: 'array',
      importance: 'optional',
      description: 'Supported API frameworks (VPAID, OMID)',
      constraint: IAB_ENUM_MEMBER,
    },
    {
      name: 'video.delivery',
      valueType: 'array',
      importance: 'optional',
      description: 'Delivery method (streaming/progressive)',
      constraint: IAB_ENUM_MEMBER,
    },
    {
      name: 'pmp.private_auction',
      valueType: 'number',
      importance: 'optional',
      description: '1 = restrict to deals',
      constraint: FLAG_0_1,
    },
    { name: 'pmp.deals', valueType: 'array', importance: 'optional', description: 'Direct deals (id, bidfloor, …)' },
  ],

  site: [
    { name: 'id', valueType: 'string', importance: 'optional', description: 'Exchange site ID' },
    { name: 'name', valueType: 'string', importance: 'optional', description: 'Site name' },
    {
      name: 'domain',
      valueType: 'string',
      importance: 'recommended',
      description: 'Site domain',
      constraint: HOSTNAME,
    },
    {
      name: 'page',
      value: { from: 'derived', source: 'url' },
      valueType: 'string',
      importance: 'recommended',
      description: 'URL of the page',
    },
    { name: 'ref', valueType: 'string', importance: 'optional', description: 'Referrer URL', constraint: ABSOLUTE_URL },
    { name: 'cat', valueType: 'array', importance: 'optional', description: 'IAB content categories' },
    {
      name: 'mobile',
      valueType: 'number',
      importance: 'optional',
      description: '1 = optimized for mobile',
      constraint: FLAG_0_1,
    },
    {
      name: 'publisher.id',
      value: { from: 'derived', source: 'publisher_id' },
      valueType: 'string',
      importance: 'recommended',
      description: 'Publisher ID',
    },
    {
      name: 'publisher.name',
      value: { from: 'derived', source: 'publisher_name' },
      valueType: 'string',
      importance: 'optional',
      description: 'Publisher name',
    },
    {
      name: 'content.title',
      value: { from: 'derived', source: 'content_title' },
      valueType: 'string',
      importance: 'optional',
      description: 'Content title',
    },
    {
      name: 'content.genre',
      value: { from: 'derived', source: 'genre' },
      valueType: 'string',
      importance: 'optional',
      description: 'Content genre',
    },
    {
      name: 'content.contentrating',
      value: { from: 'derived', source: 'rating' },
      valueType: 'string',
      importance: 'optional',
      description: 'Content rating (e.g. PG-13)',
    },
    {
      name: 'content.language',
      value: { from: 'derived', source: 'language' },
      valueType: 'string',
      importance: 'optional',
      description: 'Content language (ISO-639-1)',
    },
    {
      name: 'content.len',
      value: { from: 'derived', source: 'content_length' },
      valueType: 'number',
      importance: 'optional',
      description: 'Content length (s)',
    },
    { name: 'content.cat', valueType: 'array', importance: 'optional', description: 'Content IAB categories' },
    {
      name: 'content.livestream',
      valueType: 'number',
      importance: 'optional',
      description: '1 = live',
      constraint: FLAG_0_1,
    },
  ],

  app: [
    { name: 'id', valueType: 'string', importance: 'optional', description: 'Exchange app ID' },
    {
      name: 'name',
      value: { from: 'derived', source: 'app_name' },
      valueType: 'string',
      importance: 'optional',
      description: 'App name',
    },
    {
      name: 'bundle',
      value: { from: 'derived', source: 'app_bundle' },
      valueType: 'string',
      importance: 'recommended',
      description: 'App bundle / package ID',
    },
    { name: 'domain', valueType: 'string', importance: 'optional', description: 'App domain', constraint: HOSTNAME },
    {
      name: 'storeurl',
      value: { from: 'derived', source: 'app_store_url' },
      valueType: 'string',
      importance: 'recommended',
      description: 'App store URL',
    },
    { name: 'ver', valueType: 'string', importance: 'optional', description: 'App version' },
    { name: 'cat', valueType: 'array', importance: 'optional', description: 'IAB content categories' },
    {
      name: 'publisher.id',
      value: { from: 'derived', source: 'publisher_id' },
      valueType: 'string',
      importance: 'recommended',
      description: 'Publisher ID',
    },
    {
      name: 'publisher.name',
      value: { from: 'derived', source: 'publisher_name' },
      valueType: 'string',
      importance: 'optional',
      description: 'Publisher name',
    },
    {
      name: 'content.title',
      value: { from: 'derived', source: 'content_title' },
      valueType: 'string',
      importance: 'optional',
      description: 'Content title',
    },
    {
      name: 'content.genre',
      value: { from: 'derived', source: 'genre' },
      valueType: 'string',
      importance: 'optional',
      description: 'Content genre',
    },
    {
      name: 'content.contentrating',
      value: { from: 'derived', source: 'rating' },
      valueType: 'string',
      importance: 'optional',
      description: 'Content rating (e.g. PG-13)',
    },
    {
      name: 'content.language',
      value: { from: 'derived', source: 'language' },
      valueType: 'string',
      importance: 'optional',
      description: 'Content language (ISO-639-1)',
    },
    {
      name: 'content.len',
      value: { from: 'derived', source: 'content_length' },
      valueType: 'number',
      importance: 'optional',
      description: 'Content length (s)',
    },
    { name: 'content.cat', valueType: 'array', importance: 'optional', description: 'Content IAB categories' },
    {
      name: 'content.livestream',
      valueType: 'number',
      importance: 'optional',
      description: '1 = live',
      constraint: FLAG_0_1,
    },
  ],

  device: [
    {
      name: 'ua',
      value: { from: 'derived', source: 'ua' },
      valueType: 'string',
      importance: 'recommended',
      description: 'User agent',
    },
    {
      name: 'ip',
      value: { from: 'derived', source: 'ip' },
      valueType: 'string',
      importance: 'recommended',
      description: 'IPv4 address',
    },
    { name: 'ipv6', valueType: 'string', importance: 'optional', description: 'IPv6 address', constraint: IP_ADDRESS },
    {
      name: 'ifa',
      value: { from: 'derived', source: 'ifa' },
      valueType: 'string',
      importance: 'recommended',
      description: 'Advertising ID (IDFA/GAID/RIDA)',
    },
    {
      name: 'lmt',
      value: { from: 'derived', source: 'lmt' },
      valueType: 'number',
      importance: 'recommended',
      description: 'Limit ad tracking (0/1)',
    },
    {
      name: 'dnt',
      value: { from: 'derived', source: 'dnt' },
      valueType: 'number',
      importance: 'optional',
      description: 'Do not track (0/1)',
    },
    {
      name: 'devicetype',
      valueType: 'number',
      importance: 'recommended',
      description: 'Device type (3=CTV, 7=STB…)',
      constraint: IAB_ENUM_MEMBER,
    },
    {
      name: 'make',
      value: { from: 'derived', source: 'device_make' },
      valueType: 'string',
      importance: 'optional',
      description: 'Device make (e.g. Roku)',
    },
    {
      name: 'model',
      value: { from: 'derived', source: 'device_model' },
      valueType: 'string',
      importance: 'optional',
      description: 'Device model',
    },
    {
      name: 'os',
      value: { from: 'derived', source: 'os' },
      valueType: 'string',
      importance: 'optional',
      description: 'Operating system',
    },
    {
      name: 'osv',
      value: { from: 'derived', source: 'osv' },
      valueType: 'string',
      importance: 'optional',
      description: 'OS version',
    },
    {
      name: 'hwv',
      value: { from: 'derived', source: 'hwv' },
      valueType: 'string',
      importance: 'optional',
      description: 'Hardware version',
    },
    { name: 'w', valueType: 'number', importance: 'optional', description: 'Screen width (px)', constraint: POSITIVE },
    { name: 'h', valueType: 'number', importance: 'optional', description: 'Screen height (px)', constraint: POSITIVE },
    {
      name: 'language',
      valueType: 'string',
      importance: 'optional',
      description: 'Browser language (ISO-639-1)',
      constraint: LANGUAGE_CODE,
    },
    { name: 'carrier', valueType: 'string', importance: 'optional', description: 'Carrier / ISP' },
    {
      name: 'connectiontype',
      valueType: 'number',
      importance: 'optional',
      description: 'Network connection type',
      constraint: CONNECTION_TYPE,
    },
    {
      name: 'geo.country',
      valueType: 'string',
      importance: 'optional',
      description: 'Country (ISO-3166-1 a-3)',
      constraint: COUNTRY_CODE,
    },
    { name: 'geo.region', valueType: 'string', importance: 'optional', description: 'Region code' },
    { name: 'geo.lat', valueType: 'number', importance: 'optional', description: 'Latitude', constraint: LATITUDE },
    { name: 'geo.lon', valueType: 'number', importance: 'optional', description: 'Longitude', constraint: LONGITUDE },
    { name: 'geo.type', valueType: 'number', importance: 'optional', description: 'Location source' },
  ],

  user: [
    { name: 'id', valueType: 'string', importance: 'optional', description: 'Exchange user ID' },
    { name: 'buyeruid', valueType: 'string', importance: 'optional', description: 'Buyer-specific user ID' },
    { name: 'yob', valueType: 'number', importance: 'optional', description: 'Year of birth' },
    { name: 'gender', valueType: 'string', importance: 'optional', description: 'Gender (M/F/O)' },
    { name: 'keywords', valueType: 'string', importance: 'optional', description: 'Comma-separated keywords' },
    {
      name: 'geo.country',
      valueType: 'string',
      importance: 'optional',
      description: 'Country (ISO-3166-1 a-3)',
      constraint: COUNTRY_CODE,
    },
    { name: 'data', valueType: 'array', importance: 'optional', description: 'Data segments' },
    // Privacy and identity signals that moved out of `ext` in 2.6 — see the note on `regs` below.
    // **Linked in `4b-ii`** (`#914` tier 3). The driver writes `resolveContextValue('gdpr_consent')`
    // here at both versions' locations (`buildProceduralUser`), so the claim is certain — the rule
    // `1e` held the links to. With it the TC-string grammar reaches both rows through the concept.
    ...movedOutOfExt('consent', {
      value: { from: 'derived', source: 'gdpr_consent' },
      valueType: 'string',
      importance: 'recommended',
      description: 'GDPR TC string',
    }),
    ...movedOutOfExt('eids', { valueType: 'array', importance: 'optional', description: 'Extended user IDs' }),
  ],

  source: [
    {
      name: 'fd',
      valueType: 'number',
      importance: 'optional',
      description: '1 = final decision by exchange',
      constraint: FLAG_0_1,
    },
    { name: 'tid', valueType: 'string', importance: 'optional', description: 'Transaction ID' },
    { name: 'pchain', valueType: 'string', importance: 'optional', description: 'Payment ID chain' },
    ...movedOutOfExt('schain', { valueType: 'object', importance: 'recommended', description: 'Supply chain object' }),
  ],

  /**
   * **Seven signals moved out of `ext` into their parent in OpenRTB 2.6, and each is a version-bounded
   * pair** (#1315, the first real `when` in this catalog, #1308).
   *
   * `regs.ext.gdpr` and `regs.gdpr` are one signal in two versions' locations. So each is declared
   * twice: the `ext` spelling applies **below** 2.6 and the parent spelling **from** 2.6, and the newer
   * row names the older one in `supersedes`. The driver writes the one location that applies to the
   * receiver and never both (Todd, 2026-09-14: *follow the version*), the `/ortb` controller reads
   * every location newest-first, and the editor treats the pair as one field. All three read these
   * rows through {@link nameForVersion} and {@link versionedNames}.
   */
  regs: [
    {
      name: 'coppa',
      value: { from: 'derived', source: 'coppa' },
      valueType: 'number',
      importance: 'optional',
      description: 'Subject to COPPA (0/1)',
    },
    ...movedOutOfExt('gdpr', {
      value: { from: 'derived', source: 'gdpr' },
      valueType: 'number',
      importance: 'recommended',
      description: 'GDPR applies (0/1)',
    }),
    ...movedOutOfExt('us_privacy', {
      value: { from: 'derived', source: 'us_privacy' },
      valueType: 'string',
      importance: 'recommended',
      description: 'CCPA / US Privacy string',
    }),
    ...movedOutOfExt('gpp', {
      valueType: 'string',
      importance: 'optional',
      description: 'Global Privacy Platform string',
    }),
    ...movedOutOfExt('gpp_sid', { valueType: 'array', importance: 'optional', description: 'GPP section IDs' }),
  ],
};

/**
 * The pair a signal that moved out of `ext` in OpenRTB 2.6 declares — the `ext` row below 2.6 and
 * the parent row from 2.6, which names the one it supersedes. Function declarations are hoisted, so
 * the catalog above can call this.
 */
function movedOutOfExt(
  name: string,
  field: Omit<OrtbCatalogField, 'name' | 'when' | 'supersedes'>,
): OrtbCatalogField[] {
  return [
    { ...field, name: `ext.${name}`, when: { protocolVersion: { below: '2.6' } } },
    { ...field, name, when: { protocolVersion: { min: '2.6' } }, supersedes: `ext.${name}` },
  ];
}

/**
 * **Every name one field has had in a section, newest first** (#1315).
 *
 * Follows `supersedes` from whichever spelling is given, so `regs.gdpr` and `regs.ext.gdpr` both
 * answer `['gdpr', 'ext.gdpr']`. A field that never moved answers its own name alone, so a caller
 * can ask without branching.
 */
export function versionedNames(section: string, name: string): string[] {
  const fields = ORTB_FIELD_CATALOG[section] ?? [];
  // The newest spelling is the one nothing supersedes.
  let newest = name;
  for (let guard = 0; guard < fields.length; guard += 1) {
    const newer = fields.find((f) => f.supersedes === newest);
    if (!newer) break;
    newest = newer.name;
  }
  const names = [newest];
  for (let guard = 0; guard < fields.length; guard += 1) {
    const older = fields.find((f) => f.name === names[names.length - 1])?.supersedes;
    if (!older || names.includes(older)) break;
    names.push(older);
  }
  return names;
}

/**
 * **The one spelling of a field this receiver's version reads**, or `undefined` if none applies.
 *
 * Evaluated with `appliesTo`, the one evaluator of `when` (`D35`). A field declared without `when`
 * applies to every version and answers its own name.
 */
export function nameForVersion(section: string, name: string, version?: string): string | undefined {
  const fields = ORTB_FIELD_CATALOG[section] ?? [];
  return versionedNames(section, name).find((candidate) => {
    const field = fields.find((f) => f.name === candidate);
    return !field || appliesTo(field, { protocolVersion: version });
  });
}

/** The value at a catalog name (`ext.gdpr`) on a section object, or `undefined`. */
export function readCatalogField(host: unknown, name: string): unknown {
  let current: unknown = host;
  for (const part of name.split('.')) {
    if (current === null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

/** Set a catalog name (`ext.gdpr`) on a section object, creating intermediate objects. */
export function writeCatalogField(host: Record<string, unknown>, name: string, value: unknown): void {
  const parts = name.split('.');
  let current = host;
  for (const part of parts.slice(0, -1)) {
    const next = current[part];
    if (next === null || typeof next !== 'object' || Array.isArray(next)) current[part] = {};
    current = current[part] as Record<string, unknown>;
  }
  current[parts[parts.length - 1]] = value;
}

/**
 * **The sections the guided tree offers**, in the order it offers them.
 *
 * **`request` was missing until `#1253`**, and the omission is why the top of a bid request had no
 * authoring path at all: the Add flow iterates this list, so `tmax`, `at`, `cur` and the five
 * block-lists (`bcat`, `badv`, `bapp`, `wseat`, `wlang`) could not be added — and `bcat`/`badv` are
 * brand-safety controls nobody would argue bidBrain owns.
 *
 * **This and `ORTB_FIELD_CATALOG`'s keys are two hand-kept lists of one vocabulary**, and they
 * disagreed in *both* directions: the catalog had `request` and no `ext`, this had `ext` and no
 * `request`. `ortb-field-catalog.test.ts` now asserts every catalog key appears here, which is the
 * direction that matters — a section with no catalog fields (`ext`, which is free-form by
 * definition) is legitimate, a catalog group with no section is unreachable.
 *
 * `request` leads because it is the request's own envelope: the fields that are not inside anything.
 */
export const ORTB_STANDARD_SECTIONS = ['request', 'imp', 'app', 'site', 'device', 'user', 'source', 'regs', 'ext'];

/**
 * Empty-state "recommended body" starter sections — mode-dependent, because the
 * driver's procedural base (`OpenRtbDriver.buildBidRequest`) differs from a
 * from-scratch body:
 *
 * - **replace** — the template IS the whole request, so seed a full baseline
 *   CTV/OTT video body. `app` (not `site`) is the CTV default and avoids the
 *   mutually-exclusive site/app warning.
 * - **merge** — the driver already builds `id`/`imp`/`device`/`site|app` and the
 *   auction scalars from the session, so seeding those would be redundant (and
 *   seeding `app` on a site-context session would create BOTH → the exclusivity
 *   warning). Seed only what the driver can't derive: privacy signals (`regs`)
 *   and supply chain (`source`). Exchange-specific device/app fields (e.g.
 *   `device.lmt`, `app.storeurl`) can be layered on per-section via Add field.
 *
 * Each section contributes its required + recommended fields (see
 * {@link getMissingStandardFields}).
 */
export const ORTB_REPLACE_STARTER_SECTIONS = ['imp', 'app', 'device', 'regs', 'source'];

/**
 * **`ORTB_MERGE_STARTER_SECTIONS` is gone** (`#1163`, 2026-08-28), and the reason is the one
 * `driverMergeBaseCovers` below already records for its own predecessor.
 *
 * It was `['regs', 'source']`: a hand-kept **section** list answering *what does the driver not
 * build*, which is a claim about the driver's code. `#1117` replaced that claim with a per-field
 * declaration asserted against real requests, and wired it into **validation** and not into the
 * starter — one function apart, so the starter kept the stale shape.
 *
 * Measured cost: exactly `imp.video.placement`. The driver built no placement then, so validation warned
 * *"recommended but isn't set — the driver won't add it"* on a body the starter had just produced.
 * Nothing was over-seeded, which is why it stayed invisible — the list was never wrong, only
 * **short**, which no inclusion test can see.
 *
 * **Corrected 2026-09-15** (`#1318`): since `#1112` the driver renders the placement concept on every
 * request, so the same warning became false — the declaration names the concept at `plcmt` and
 * `driverMergeBaseCovers` looked up `placement` literally. `mergeBaseFieldAt` now answers a concept
 * under every spelling.
 *
 * `buildStarterLeaves` now asks `driverMergeBaseCovers` per field. `ORTB_REPLACE_STARTER_SECTIONS`
 * stays a list because it answers a different question — *what does a complete request need* — and
 * that vocabulary is ours.
 */

/**
 * **Version scoping is a `when` on the declaration** (`D35`, `#1163`) — this is where the map that
 * used to live here went.
 *
 * It was `Record<OrtbVersion, Record<string, OrtbFieldImportance>>`: a second copy of part of the
 * field list, keyed by version, with `fieldsForVersion` applying it. Its own comment called future
 * deltas *"a one-line addition rather than a fork of the catalog"* while being exactly that — a fork
 * of the catalog, deferred. The two entries it re-weighted now say when they apply, next to
 * everything else they declare, and `appliesTo` is the only thing that reads it.
 *
 * **What it re-weighted is gone, and this is the record of that** (`N11h`, #1112): the two entries
 * were the 2.5/2.6 placement pair, and they are now one concept entry whose *members* are
 * version-scoped.
 *
 * **The mechanism went live with #1315.** The seven privacy signals OpenRTB 2.6 moved out of `ext`
 * are the first genuinely version-scoped **fields** (as opposed to a version-scoped vocabulary), and
 * they declare `when` on each row of their pair — see `movedOutOfExt`.
 */

/**
 * Normalize a free-form version string to a known `OrtbVersion` (defaults to 2.6).
 *
 * **Delegates rather than re-deciding.** The driver has to answer the same question to pick
 * between `video.placement` and `video.plcmt` (D29), and a console copy free to disagree
 * would weight a catalog for one version while the wire spoke the other — the editor
 * telling the operator something the request contradicts.
 */
export function normalizeOrtbVersion(version?: string): OrtbVersion {
  return normalizeProtocolVersion(version);
}

/**
 * A section's fields with per-version importance applied — a field that does not apply to this
 * version is de-weighted rather than hidden, because an exchange ignores a field it does not
 * know and **missing the right one is a real gap**.
 *
 * The privacy pairs that moved out of `ext` in 2.6 are what declares a `when` (#1315): on a 2.6
 * source the `ext.*` row is de-weighted, and the parent row is recommended.
 *
 * **Exported because a reader that walks `ORTB_FIELD_CATALOG` directly never sees `when`.** The
 * body validator's coverage sweep did exactly that after #1315, and demanded both halves of every
 * pair on every source (`regs.gdpr` *and* `regs.ext.gdpr`) while the add-field list beside it,
 * which came through here, offered one.
 */
export function fieldsForVersion(section: string, version?: OrtbVersion | string): OrtbCatalogField[] {
  const fields = ORTB_FIELD_CATALOG[section] ?? [];
  // Normalised rather than short-circuited on `null`: an unstated version means the authored
  // default (2.6), which is what returning the base list used to mean implicitly.
  const protocolVersion = normalizeOrtbVersion(version);
  return fields.map((f) => (appliesTo(f, { protocolVersion }) ? f : { ...f, importance: 'optional' }));
}

/**
 * **Every spelling of a concept path, including the one it is written in** (#1112,
 * 2026-08-27).
 *
 * `video.placement` and `video.plcmt` are one field asked for two ways: the catalog offers
 * the concept under one name, the row renders the receiver's, the raw JSON tab shows
 * whichever the document stores, and an operator reading their exchange's 2.6 integration
 * guide knows only `plcmt`. Any of those is a legitimate way to look for it, so all of them
 * find it.
 *
 * Derived from the declaration rather than hand-kept — *the tell is a hand-maintained list
 * of things somebody else defines*, and the IAB owns this one. Scoped by the concept's `on`
 * object so a custom `ext.placement` is not swept in: the driver renders concepts on
 * `imp[].video` and nowhere else.
 *
 * Returns the path unchanged when it names no concept, so a caller can ask without branching.
 */
export function fieldSpellings(relPath: string): string[] {
  // **A field that moved location between versions is one field too** (#1315): a body carrying
  // `regs.ext.gdpr` has a GDPR flag, so offering `regs.gdpr` beside it would put two rows on one
  // signal. Only declared pairs count — a row with no `supersedes` link is never swept in.
  for (const section of Object.keys(ORTB_FIELD_CATALOG)) {
    const names = versionedNames(section, relPath);
    if (names.length > 1) return names;
  }

  const segments = relPath.split('.');
  const leaf = segments[segments.length - 1];
  const parent = segments.slice(0, -1);
  const concept = protocolConceptForWireField(leaf);
  if (!concept || parent[parent.length - 1] !== concept.on) return [relPath];

  const names = new Set([concept.concept, ...wireFieldNamesFor(concept.concept)]);
  return [...names].map((name) => [...parent, name].join('.'));
}

/**
 * The field name this version actually puts on the wire for a concept path, or `undefined`
 * where the path names no concept.
 *
 * This is what the row is **named** after (#1112): a 2.6 source authoring the placement
 * concept sends `plcmt`, and a row headed `placement` was the confusion the one-entry
 * catalog introduced — the operator read the editor, then read the request, and the two did
 * not use the same word.
 */
export function conceptWireName(relPath: string, version?: OrtbVersion | string): string | undefined {
  const segments = relPath.split('.');
  const concept = protocolConceptForWireField(segments[segments.length - 1]);
  if (!concept || segments[segments.length - 2] !== concept.on) return undefined;
  return wireExpressionFor(concept.concept, concept.assumed, normalizeOrtbVersion(version))?.field;
}

/**
 * What an authored member becomes for this version — the row's *resolution*, and the reason
 * a concept row is the one static leaf whose resolution is **not** itself.
 *
 * `renderConceptMember` is the driver's own evaluator, reached through the shared catalog:
 * an editor that computed `plcmt: 1` separately would eventually show a value we do not
 * send, and *a value reads as proof* (#1002).
 */
export function conceptRenderingAt(
  relPath: string,
  member: unknown,
  version?: OrtbVersion | string,
): ConceptRendering | undefined {
  const segments = relPath.split('.');
  const concept = protocolConceptForWireField(segments[segments.length - 1]);
  if (!concept || segments[segments.length - 2] !== concept.on) return undefined;
  if (typeof member !== 'string' || member.trim() === '') return undefined;
  return renderConceptMember(concept.concept, member.trim(), normalizeOrtbVersion(version));
}

/**
 * Look up a single catalog field by section + section-relative path.
 *
 * Matched through {@link fieldSpellings}, so a leaf stored at `video.plcmt` — which the raw
 * JSON tab and a pasted body can both produce — resolves to the one placement entry rather
 * than to nothing. It resolving to nothing is what made such a leaf typeless, memberless and
 * un-seeded while looking like an unknown custom field.
 */
export function getCatalogField(section: string, relPath: string): OrtbCatalogField | undefined {
  const fields = ORTB_FIELD_CATALOG[section] ?? [];
  const spellings = fieldSpellings(relPath);
  return fields.find((f) => spellings.includes(f.name));
}

/**
 * **What the catalogue declares at a body path** — the address as a body stores it, patterns and array
 * indices included (`imp.$each.video.w`, `imp.$at.0.video.w`, `imp.0.video.w`).
 *
 * It was the console's alone (`catalogFieldForPath`) until `#1430`, when the driver needed the same
 * answer to convert a leaf to the type its slot declares (`FD13`). Two readers of *where does this
 * address live in the catalogue* is the per-caller copy the overriding rule is about, so both call
 * this. An index is a string in the driver's walk and a number in the console's, and neither names a
 * field. A top-level field lives in the `request` section, which is not part of the path (`#1194`).
 */
export function ortbCatalogFieldAtPath(path: readonly (string | number)[]): OrtbCatalogField | undefined {
  const named: string[] = [];
  for (let i = 0; i < path.length; i++) {
    const segment = path[i];
    if (typeof segment !== 'string' || /^\d+$/.test(segment) || segment === ORTB_BODY_PATTERN_EACH) continue;
    if (segment === ORTB_BODY_PATTERN_AT) {
      i++; // the key naming which element is an address, not a field
      continue;
    }
    named.push(segment);
  }
  if (named.length === 0) return undefined;
  if (named.length === 1) return getCatalogField('request', named[0]);
  const [section, ...rest] = named;
  return getCatalogField(section, rest.join('.'));
}

/**
 * **The type a value at this body path is sent as, where no row Type says otherwise** (`FD13`) — the
 * field's declared type, or `null` where nothing converts to it.
 *
 * `null` for a field that declares no type, and for a **concept-valued** field
 * (`value.from === 'concept'`). That one declares `string` because its authoring vocabulary is member
 * names (`instream`), which the driver renders to the receiver's wire value; a number authored there is
 * the wire value itself, the author's escape hatch. Converting either to text would break both, so the
 * concept renderer owns that field's conversion. The driver, the value check and the `auto` label all
 * read this, so the three cannot disagree.
 */
export function ortbSentTypeAtPath(path: readonly (string | number)[]): DestinationFieldType | null {
  return ortbFieldSentType(ortbCatalogFieldAtPath(path));
}

/** {@link ortbSentTypeAtPath} for a field already in hand — the value sweep walks fields, not paths. */
export function ortbFieldSentType(
  field: Pick<OrtbCatalogField, 'valueType' | 'value'> | undefined,
): DestinationFieldType | null {
  if (!field?.valueType || field.value?.from === 'concept') return null;
  return field.valueType;
}

/**
 * Does the driver populate this section-relative field from the session in **merge** mode?
 *
 * **`ORTB_DRIVER_MERGE_BASE` is gone from here** (#1117). It answered per *section* —
 * `imp: '*'`, `site: '*'`, `app: '*'`, and a seven-name list for `device` — and it was the
 * console's own copy of a claim about the driver's code, which is the kind that rots without a
 * symptom. Measured 2026-08-27: `imp: '*'` is false (`video.playbackend` is built by nothing),
 * and *the driver sets `tagid`* is not *the driver sets `tagid` correctly for this exchange* —
 * which is what let the body paste discard a partner's `tagid`, `protocols` and `minduration`
 * under the heading *driver-managed* (#1187).
 *
 * `ORTB_MERGE_BASE` replaces it: per field, with a sentence for the operator, and asserted
 * against real requests by `libs/drivers/ortb-merge-base.spec.ts`. Validation asks it the same
 * question it used to ask the map — *will this be populated anyway* — and now gets an answer
 * that is checked.
 */
export function driverMergeBaseCovers(section: string, relPath: string): boolean {
  return isPopulatedWithoutAuthoring(mergeBaseFieldAt(`${section}.${relPath}`));
}

/**
 * Is a catalog field already present given the section's present relative paths?
 * A field counts as present when a leaf sits exactly at it **or below it** — so a
 * nested `ext.schain.foo` marks the object-typed `ext.schain` field as present
 * (and it won't be re-suggested or double-counted as "recommended, missing").
 */
function isFieldPresent(fieldPath: string, presentRelPaths: Set<string>): boolean {
  // **Every spelling counts as present** (#1112). A body that already carries `video.plcmt`
  // has a placement; offering to add `video.placement` beside it would put two rows on one
  // concept, and the driver reads whichever it finds first — an editor that produces an
  // ambiguity the wire then resolves silently.
  for (const spelling of fieldSpellings(fieldPath)) {
    if (presentRelPaths.has(spelling)) return true;
    const prefix = `${spelling}.`;
    for (const p of presentRelPaths) if (p.startsWith(prefix)) return true;
  }
  return false;
}

/**
 * Field suggestions for a section's add-field autocomplete: catalog fields not
 * already present, filtered by the (case-insensitive) query, standard fields
 * (required/recommended) first.
 */
export function getFieldSuggestions(
  section: string,
  query: string,
  presentRelPaths: Set<string>,
  version?: OrtbVersion | string,
): OrtbCatalogField[] {
  const fields = fieldsForVersion(section, version);
  const q = query.trim().toLowerCase();
  const rank: Record<FieldImportance, number> = { required: 0, recommended: 1, optional: 2 };
  return (
    fields
      .filter((f) => !isFieldPresent(f.name, presentRelPaths))
      // **Matched against every spelling** (#1112): the catalog offers `video.placement`, a 2.6
      // exchange's integration guide says `plcmt`, and typing what the guide says used to return
      // nothing — which reads as *this editor has no such field* rather than *it is called
      // something else here*.
      .filter((f) => (q ? fieldSpellings(f.name).some((name) => name.toLowerCase().includes(q)) : true))
      .sort((a, b) => rank[a.importance] - rank[b.importance] || a.name.localeCompare(b.name))
  );
}

/** A field this version reads that is authored at another version's location — see {@link getMoveOffers}. */
export interface OrtbMoveOffer {
  field: OrtbCatalogField;
  /** The section-relative name it is authored at now (`ext.gdpr`). */
  from: string;
}

/**
 * **The fields the add-field list offers to move rather than add** (Todd, 2026-09-28: *"add the 'move
 * offer', I like that"*).
 *
 * {@link getFieldSuggestions} leaves out a field present under any spelling, deliberately (`#1315`): a
 * body carrying `regs.ext.gdpr` has a GDPR flag, and adding `regs.gdpr` beside it would put the signal
 * on the wire twice. But that made typing `gdpr` answer with nothing, which reads as *this editor has
 * no such field*. So the field this version reads is offered **as a move** of the one already authored
 * — the same move the finding's Fix makes.
 *
 * Only declared version pairs, and only the name this version reads: a concept spelling
 * (`placement`/`plcmt`) is one field the driver renders under either name, so there is nothing to move.
 */
export function getMoveOffers(
  section: string,
  query: string,
  presentRelPaths: Set<string>,
  version: OrtbVersion | string | null,
): OrtbMoveOffer[] {
  const q = query.trim().toLowerCase();
  const present = (name: string) =>
    presentRelPaths.has(name) || [...presentRelPaths].some((p) => p.startsWith(`${name}.`));
  const offers: OrtbMoveOffer[] = [];
  for (const field of fieldsForVersion(section, version ?? undefined)) {
    if (nameForVersion(section, field.name, version ?? undefined) !== field.name || present(field.name)) continue;
    const names = versionedNames(section, field.name);
    const from = names.find((name) => name !== field.name && present(name));
    if (!from) continue;
    if (q && !names.some((name) => name.toLowerCase().includes(q))) continue;
    offers.push({ field, from });
  }
  return offers;
}

/**
 * The required/recommended fields for a section that are not yet present — what *Add recommended*
 * offers and counts, what the starter body seeds, and what a fix adds.
 *
 * **`version` and `mode` are required** (`#1318` item 2). Both decide the answer, and each was
 * optional or absent somewhere: *Add N recommended* offered the fields the driver already builds in
 * merge mode — 19 of them — while validation and the starter filtered those out, and the starter
 * read the catalog with no version at all, so a 2.5 source was seeded 2.6's half of every
 * version-bounded pair. A reader that can be called without the question it needs answers a
 * different one (`#1254`).
 *
 * `mode` is the body mode: in `merge` the driver builds a base underneath the template, so a field
 * it supplies unaided is not missing (`driverMergeBaseCovers`). A `forwarded-if-sent` field is still
 * offered, because it goes out only when the publisher sends one.
 */
export function getMissingStandardFields(
  section: string,
  presentRelPaths: Set<string>,
  version: OrtbVersion | string | null,
  mode: 'merge' | 'replace',
): OrtbCatalogField[] {
  return fieldsForVersion(section, version ?? undefined).filter(
    (f) =>
      f.importance !== 'optional' &&
      !isFieldPresent(f.name, presentRelPaths) &&
      !(mode === 'merge' && driverMergeBaseCovers(section, f.name)),
  );
}

/**
 * **Which body field an ORTB query parameter also fills** (D18, lifted in slice `1e` for `#944`).
 *
 * On an ORTB source a request-parameter row is a query parameter, and a row whose concept has a home
 * in the bid request fills that too, from the same resolution — *"which is what makes the two agree
 * by construction rather than by review"* (`#927`/`#1006`). Until `1e` the only statement of *which
 * names, which fields* was a hand-kept switch inside `OpenRtbDriver.applyKnownFieldValue`: 21 names,
 * readable by the driver and by nothing else.
 *
 * So the console could not know that a `ua` parameter and a `device.ua` body leaf are the same slot,
 * and could not warn when they disagree — which on the wire is worse than two values sent, because one
 * **overwrites** the other. Lifted here, the driver applies it and the console reads it, and a
 * parameter's concept is its body fields' own declared link (`value: { from: 'derived' }` on
 * `ORTB_FIELD_CATALOG`), never a guess from its name.
 *
 * Three behaviours are part of the declaration because the driver has always had them:
 * - **`number` fields** are parsed, and a value that is absent or does not parse **removes** the field
 *   (`w`, `h`, `bidfloor`); a `string` field that resolves to nothing is left alone.
 * - An **`imp`** field is written to every impression, and `video.*` only where the impression has one.
 * - An **`app`** field switches the request to app context: `site` is removed.
 */

/** A body field a parameter fills, by catalog section and section-relative name. */
export interface OrtbParamTarget {
  readonly section: 'device' | 'site' | 'app' | 'imp';
  readonly name: string;
}

export interface OrtbParamRoute {
  /** The parameter names, lower-case, that fill these fields. */
  readonly names: readonly string[];
  readonly targets: readonly OrtbParamTarget[];
  readonly kind: 'string' | 'number';
}

export const ORTB_PARAM_ROUTES: readonly OrtbParamRoute[] = [
  { names: ['ip'], targets: [{ section: 'device', name: 'ip' }], kind: 'string' },
  { names: ['ua', 'user_agent'], targets: [{ section: 'device', name: 'ua' }], kind: 'string' },
  { names: ['ifa', 'idfa', 'did', 'device_id'], targets: [{ section: 'device', name: 'ifa' }], kind: 'string' },
  {
    names: ['w', 'width', 'player_width'],
    targets: [
      { section: 'device', name: 'w' },
      { section: 'imp', name: 'video.w' },
    ],
    kind: 'number',
  },
  {
    names: ['h', 'height', 'player_height'],
    targets: [
      { section: 'device', name: 'h' },
      { section: 'imp', name: 'video.h' },
    ],
    kind: 'number',
  },
  { names: ['url', 'page'], targets: [{ section: 'site', name: 'page' }], kind: 'string' },
  { names: ['domain'], targets: [{ section: 'site', name: 'domain' }], kind: 'string' },
  { names: ['app_bundle', 'bundle'], targets: [{ section: 'app', name: 'bundle' }], kind: 'string' },
  { names: ['app_name'], targets: [{ section: 'app', name: 'name' }], kind: 'string' },
  { names: ['bidfloor', 'floor'], targets: [{ section: 'imp', name: 'bidfloor' }], kind: 'number' },
];

/** The route a parameter name takes into the body, or `undefined` where it is a query parameter only. */
export function ortbParamRoute(name: string): OrtbParamRoute | undefined {
  const key = name.trim().toLowerCase();
  return ORTB_PARAM_ROUTES.find((route) => route.names.includes(key));
}
